import { useState, type FormEvent } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { COMPANIONS, MISSION_MODES, YEAR_LEVEL_LABELS, type AdminChildProfile } from '@qq/shared';
import { ApiRequestError, api } from '../lib/api.js';
import { childPhotoUrl } from '../lib/session.js';
import { Avatar } from '../art/Avatar.js';

/**
 * Profile management. Everything about a child is edited here - nothing is
 * hard-coded anywhere in the app, which is why this screen can create a child
 * from nothing and archive one without deleting a single thing they have done.
 */
export function AdminChildren() {
  const qc = useQueryClient();
  const [creating, setCreating] = useState(false);

  const children = useQuery({
    queryKey: ['admin', 'children'],
    queryFn: () => api<AdminChildProfile[]>('/admin/children?includeArchived=true'),
  });

  const family = useQuery({
    queryKey: ['admin', 'family'],
    queryFn: () => api<{ photoAvatarsEnabled: boolean }>('/admin/family'),
  });

  const setPhotos = useMutation({
    mutationFn: (enabled: boolean) =>
      api('/admin/family', { method: 'PATCH', body: { photoAvatarsEnabled: enabled } }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['admin', 'family'] });
      void qc.invalidateQueries({ queryKey: ['admin', 'children'] });
    },
  });

  return (
    <div className="admin">
      <div className="admin-header">
        <div>
          <h1>Children</h1>
          <p className="muted">Profiles, interests, targets and PINs.</p>
        </div>
        <button type="button" className="btn" onClick={() => setCreating((v) => !v)}>
          {creating ? 'Cancel' : 'Add a child'}
        </button>
      </div>

      {creating && <CreateChildForm onDone={() => setCreating(false)} />}

      <section className="card">
        <h2>Photo characters</h2>
        <label className="switch-row">
          <input
            type="checkbox"
            checked={family.data?.photoAvatarsEnabled ?? false}
            onChange={(e) => setPhotos.mutate(e.target.checked)}
          />
          <span>Let the children use a selfie as their character</span>
        </label>
        <p className="muted">
          Photos are stored on this server only. They are never shown on the sign-in screen, never
          visible to the other child, and never sent to any outside service. Turning this off hides
          the camera and stops every photo being served, without deleting anything.
        </p>
      </section>

      {children.isPending && <p className="muted">Loading…</p>}
      {children.data?.map((child) => (
        <ChildCard key={child.id} child={child} photosAllowed={family.data?.photoAvatarsEnabled} />
      ))}
    </div>
  );
}

function ChildCard({
  child,
  photosAllowed,
}: {
  child: AdminChildProfile;
  photosAllowed: boolean | undefined;
}) {
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const invalidate = () => qc.invalidateQueries({ queryKey: ['admin', 'children'] });

  const patch = useMutation({
    mutationFn: (body: Record<string, unknown>) =>
      api(`/admin/children/${child.id}`, { method: 'PATCH', body }),
    onSuccess: invalidate,
  });
  const archive = useMutation({
    mutationFn: (archived: boolean) =>
      api(`/admin/children/${child.id}/archive`, { method: 'POST', body: { archived } }),
    onSuccess: invalidate,
  });
  const deletePhoto = useMutation({
    mutationFn: () => api(`/admin/children/${child.id}/photo`, { method: 'DELETE' }),
    onSuccess: invalidate,
  });
  const resetPin = useMutation({
    mutationFn: (pin: string) =>
      api(`/admin/children/${child.id}/pin`, { method: 'POST', body: { pin } }),
    onSuccess: invalidate,
  });
  const setInterests = useMutation({
    mutationFn: (interests: { tag: string; weight: number; active: boolean }[]) =>
      api(`/admin/children/${child.id}/interests`, { method: 'PUT', body: { interests } }),
    onSuccess: invalidate,
  });

  const [pin, setPin] = useState('');
  const [newTag, setNewTag] = useState('');
  const active = child.interests.filter((i) => i.active);

  return (
    <section className="card" data-archived={child.archived}>
      <div className="child-head">
        <Avatar
          config={child.avatarConfig}
          size={72}
          photoUrl={
            child.avatarKind === 'photo' && child.hasPhoto && photosAllowed
              ? childPhotoUrl(child.id, child.photoUpdatedAt)
              : null
          }
          title={child.nickname}
        />
        <div>
          <h2>
            {child.nickname} {child.archived && <span className="pill">Archived</span>}
          </h2>
          <p className="muted">
            {child.ageYears} years old · {child.yearLevelLabel} · born {child.dob}
            {child.hasPhoto ? ' · has a photo' : ''}
          </p>
        </div>
        <button type="button" className="btn btn--quiet" onClick={() => setOpen((v) => !v)}>
          {open ? 'Close' : 'Edit'}
        </button>
      </div>

      {open && (
        <div className="child-body">
          <div className="field-row">
            <label>
              Nickname
              <input
                defaultValue={child.nickname}
                onBlur={(e) =>
                  e.target.value !== child.nickname && patch.mutate({ nickname: e.target.value })
                }
              />
            </label>
            <label>
              Date of birth
              <input
                type="date"
                defaultValue={child.dob}
                onChange={(e) => e.target.value && patch.mutate({ dob: e.target.value })}
              />
            </label>
            <label>
              Year level
              <select
                defaultValue={child.yearLevelLabel}
                onChange={(e) => patch.mutate({ yearLevelLabel: e.target.value })}
              >
                {YEAR_LEVEL_LABELS.map((y) => (
                  <option key={y}>{y}</option>
                ))}
              </select>
            </label>
            <label>
              Companion
              <select
                defaultValue={child.companionId ?? ''}
                onChange={(e) => patch.mutate({ companionId: e.target.value })}
              >
                {COMPANIONS.map((c) => (
                  <option key={c.key} value={c.key}>
                    {c.name}
                  </option>
                ))}
              </select>
            </label>
          </div>

          <p>
            <Link className="btn btn--outline" to={`/admin/children/${child.id}/phonics`}>
              Phonics level: check or set it
            </Link>
          </p>

          <div className="field-row">
            <label>
              Session length (minutes)
              <input
                type="number"
                min={5}
                max={45}
                defaultValue={child.settings.sessionSoftCapMins}
                onBlur={(e) =>
                  patch.mutate({ settings: { sessionSoftCapMins: Number(e.target.value) } })
                }
              />
            </label>
            <label>
              Missions per week
              <input
                type="number"
                min={1}
                max={14}
                defaultValue={child.settings.weeklyTargetMissions}
                onBlur={(e) =>
                  patch.mutate({ settings: { weeklyTargetMissions: Number(e.target.value) } })
                }
              />
            </label>
            <label>
              Mission choice
              <select
                defaultValue={child.settings.missionMode}
                onChange={(e) => patch.mutate({ settings: { missionMode: e.target.value } })}
              >
                {MISSION_MODES.map((m) => (
                  <option key={m} value={m}>
                    {m === 'prescribed' ? 'One mission, chosen for them' : 'A choice of missions'}
                  </option>
                ))}
              </select>
            </label>
            <label>
              Read questions aloud
              <select
                defaultValue={child.settings.readAloud ?? ''}
                onChange={(e) => patch.mutate({ settings: { readAloud: e.target.value || null } })}
              >
                <option value="">
                  The default for their year (now:{' '}
                  {child.readAloud === 'auto' ? 'as each question appears' : 'when they tap'})
                </option>
                <option value="auto">As each question appears</option>
                <option value="tap">When they tap the speaker</option>
                <option value="off">Never</option>
              </select>
            </label>
          </div>

          <h3>Interests</h3>
          <p className="muted">
            These theme their missions. Weight nudges how often a theme comes up.
          </p>
          <div className="tag-row">
            {active.map((interest) => (
              <span key={interest.tag} className="pill">
                {interest.tag}
                <button
                  type="button"
                  aria-label={`Remove ${interest.tag}`}
                  onClick={() => setInterests.mutate(active.filter((i) => i.tag !== interest.tag))}
                >
                  ×
                </button>
              </span>
            ))}
          </div>
          <form
            className="inline-form"
            onSubmit={(e: FormEvent) => {
              e.preventDefault();
              if (!newTag.trim()) return;
              setInterests.mutate([...active, { tag: newTag.trim(), weight: 1, active: true }]);
              setNewTag('');
            }}
          >
            <input
              value={newTag}
              placeholder="dinosaurs"
              onChange={(e) => setNewTag(e.target.value)}
            />
            <button className="btn" type="submit">
              Add
            </button>
          </form>

          <h3>PIN</h3>
          <form
            className="inline-form"
            onSubmit={(e: FormEvent) => {
              e.preventDefault();
              resetPin.mutate(pin, { onSuccess: () => setPin('') });
            }}
          >
            <input
              value={pin}
              inputMode="numeric"
              pattern="\d{4}"
              placeholder="4 digits"
              onChange={(e) => setPin(e.target.value)}
            />
            <button className="btn" type="submit" disabled={!/^\d{4}$/.test(pin)}>
              Set a new PIN
            </button>
          </form>
          {resetPin.isSuccess && (
            <p className="muted">PIN changed. Every device that child was signed in on is out.</p>
          )}
          {resetPin.error instanceof ApiRequestError && (
            <p className="error-note">{resetPin.error.body.message}</p>
          )}

          <h3>Photo</h3>
          {child.hasPhoto ? (
            <button
              type="button"
              className="btn btn--outline"
              onClick={() => deletePhoto.mutate()}
              disabled={deletePhoto.isPending}
            >
              Delete this child&rsquo;s photo
            </button>
          ) : (
            <p className="muted">No photo saved.</p>
          )}

          <h3>Archive</h3>
          <p className="muted">
            Archiving hides the profile and signs that child out. Nothing they have done is deleted.
          </p>
          <button
            type="button"
            className="btn btn--outline"
            onClick={() => archive.mutate(!child.archived)}
          >
            {child.archived ? 'Bring this profile back' : 'Archive this profile'}
          </button>
        </div>
      )}
    </section>
  );
}

function CreateChildForm({ onDone }: { onDone: () => void }) {
  const qc = useQueryClient();
  const [form, setForm] = useState({
    nickname: '',
    dob: '',
    yearLevelLabel: 'Prep',
    pin: '',
  });

  const create = useMutation({
    mutationFn: () => api('/admin/children', { method: 'POST', body: form }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['admin', 'children'] });
      onDone();
    },
  });

  const ready =
    form.nickname.trim() && /^\d{4}-\d{2}-\d{2}$/.test(form.dob) && /^\d{4}$/.test(form.pin);

  return (
    <section className="card">
      <h2>Add a child</h2>
      <div className="field-row">
        <label>
          Nickname
          <input
            value={form.nickname}
            onChange={(e) => setForm({ ...form, nickname: e.target.value })}
          />
        </label>
        <label>
          Date of birth
          <input
            type="date"
            value={form.dob}
            onChange={(e) => setForm({ ...form, dob: e.target.value })}
          />
        </label>
        <label>
          Year level
          <select
            value={form.yearLevelLabel}
            onChange={(e) => setForm({ ...form, yearLevelLabel: e.target.value })}
          >
            {YEAR_LEVEL_LABELS.map((y) => (
              <option key={y}>{y}</option>
            ))}
          </select>
        </label>
        <label>
          PIN (4 digits)
          <input
            value={form.pin}
            inputMode="numeric"
            onChange={(e) => setForm({ ...form, pin: e.target.value })}
          />
        </label>
      </div>
      {create.error instanceof ApiRequestError && (
        <p className="error-note">{create.error.body.message}</p>
      )}
      <button className="btn" disabled={!ready || create.isPending} onClick={() => create.mutate()}>
        {create.isPending ? 'Creating…' : 'Create profile'}
      </button>
    </section>
  );
}
